import { For, Show, createSignal } from "solid-js";
import { Actions, Button, IconButton, Toggle } from "./Form.tsx";
import { Modal } from "./Modal.tsx";
import { TrashIcon } from "./icons.tsx";
import { useApi } from "../api/provider.tsx";
import { useI18n } from "../i18n/provider.tsx";
import { describeError, inUseBy, type ApiError } from "../api/result.ts";
import type { Collection, Collections } from "../api/client.ts";
import type { InUseBy } from "../api/domain.ts";
import type { Column } from "./Table.tsx";
import styles from "./DeleteButton.module.scss";

export type DeleteButtonProps = {
  collection: Collection;
  id: string;
  /** What the dialog names, such as a topic's title; the id when there is none. */
  label?: string;
  disabled?: boolean;
  /** Runs once the entity is gone, so the page can reload its list. */
  onRemoved: () => void;
};

type Stage =
  | { state: "closed" }
  | { state: "asking" }
  | { state: "busy" }
  | { state: "blocked"; referrers: InUseBy[] }
  | { state: "failed"; error: ApiError };

/**
 * Deleting one entity, with the refusal turned into the next step.
 *
 * The server refuses while anything still names the entity, so the first click
 * is also the question "what would this break?". The answer becomes the dialog:
 * every referrer listed, each marked with whether it would be deleted along or
 * merely rewritten, and one checkbox to go ahead anyway.
 */
export const DeleteButton = (props: DeleteButtonProps) => {
  const api = useApi();
  const i18n = useI18n();

  const [stage, setStage] = createSignal<Stage>({ state: "closed" });
  const [force, setForce] = createSignal(false);

  const subject = () => props.label ?? props.id;
  const open = () => stage().state !== "closed";

  const close = () => {
    setStage({ state: "closed" });
    setForce(false);
  };

  const blocking = (): InUseBy[] => {
    const current = stage();
    return current.state === "blocked" ? current.referrers : [];
  };

  const failure = (): ApiError | undefined => {
    const current = stage();
    return current.state === "failed" ? current.error : undefined;
  };

  const remove = async () => {
    setStage({ state: "busy" });
    const result = await api.remove(props.collection, props.id, { force: force() });
    if (result.ok) {
      close();
      props.onRemoved();
      return;
    }

    const referrers = inUseBy(result.error);
    if (referrers === undefined) {
      setStage({ state: "failed", error: result.error });
      return;
    }
    setStage({ state: "blocked", referrers });
  };

  const busy = () => stage().state === "busy";
  const confirmLabel = () => {
    if (busy()) return i18n.t().remove.deleting;
    return force() ? i18n.t().remove.forceConfirm : i18n.t().remove.confirm;
  };

  return (
    <>
      <IconButton
        label={`${i18n.t().remove.action}: ${subject()}`}
        icon={<TrashIcon />}
        tone="danger"
        disabled={props.disabled === true}
        onClick={() => setStage({ state: "asking" })}
      />

      <Modal
        open={open()}
        title={blocking().length > 0 ? i18n.t().remove.blockedTitle : i18n.t().remove.title}
        description={subject()}
        onClose={close}
      >
        <Show when={blocking().length > 0}>
          <p class={styles.intro}>{i18n.t().remove.blockedIntro}</p>
          <ul class={styles.referrers}>
            <For each={blocking()}>
              {(reference) => (
                <li class={reference.cascades ? styles.cascades : styles.detaches}>
                  <span class={styles.referrer}>
                    {reference.entity_type} {reference.id}
                  </span>
                  <span class={styles.consequence}>
                    {reference.field} &middot;{" "}
                    {reference.cascades
                      ? i18n.t().remove.cascadeWarning
                      : i18n.t().remove.detachNote}
                  </span>
                </li>
              )}
            </For>
          </ul>
          <Toggle
            label={i18n.t().remove.forceLabel}
            checked={force()}
            onToggle={setForce}
          />
          <p class={styles.hint}>{i18n.t().remove.forceHint}</p>
        </Show>

        <Show when={failure()}>
          {(error) => (
            <p class={styles.failure} role="alert">
              {describeError(i18n.t(), error())}
            </p>
          )}
        </Show>

        <Actions>
          <Button
            label={confirmLabel()}
            tone="danger"
            disabled={busy() || (blocking().length > 0 && !force())}
            onClick={() => void remove()}
          />
          <Button label={i18n.t().common.cancel} disabled={busy()} onClick={close} />
        </Actions>
      </Modal>
    </>
  );
};

/**
 * The delete action as a table column, since every admin list wants the same
 * one in the same place. Pages that already have an actions column compose
 * `<DeleteButton>` into it instead.
 */
export const deleteColumn = <C extends Collection>(spec: {
  header: string;
  collection: C;
  id: (row: Collections[C]) => string;
  label?: (row: Collections[C]) => string;
  onRemoved: () => void;
}): Column<Collections[C]> => ({
  key: "delete",
  header: spec.header,
  align: "end",
  cell: (row) => (
    <DeleteButton
      collection={spec.collection}
      id={spec.id(row)}
      label={spec.label?.(row)}
      onRemoved={spec.onRemoved}
    />
  ),
});
